'use client'
import { type FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  LoadingButton,
} from 'base-ui'

import { FriendlyCaptchaChallenge } from '../../friendly-captcha'
import { useApiSwrForgotPasswordRequestMutation } from '../api/useApiSwrForgotPasswordRequestMutation'
import { useForgotPasswordPageContext } from '../context/ForgotPasswordPageContextProvider'
import {
  type ForgotPasswordFormState,
  makeForgotPasswordFormState,
} from '../makeForgotPasswordFormState'

const handleSubmit = async ({
  forgotPasswordSwr,
  formState,
  setEmail,
  setIsForgotPasswordFormSubmitted,
  setIsForgotPasswordRequestSuccess,
}: {
  forgotPasswordSwr: ReturnType<typeof useApiSwrForgotPasswordRequestMutation>
  formState: ForgotPasswordFormState
  setEmail: (email: string) => void
  setIsForgotPasswordFormSubmitted: (error: boolean) => void
  setIsForgotPasswordRequestSuccess: (subscribed: boolean) => void
}): Promise<void> => {
  if (forgotPasswordSwr.isMutating) {
    return
  }

  formState.validate()
  if (!formState.isValid()) {
    return
  }

  const { model } = formState

  try {
    await forgotPasswordSwr.trigger({
      email: model.email,
      fcSolution: model.fcSolution,
    })
    setEmail(model.email)
    setIsForgotPasswordFormSubmitted(true)
    setIsForgotPasswordRequestSuccess(true)
  } catch {
    setEmail('')
    setIsForgotPasswordFormSubmitted(true)
    setIsForgotPasswordRequestSuccess(false)
    formState.update()
  }
}

export const ForgotPasswordRequestForm: FC = () => {
  const {
    setEmail,
    setIsForgotPasswordFormSubmitted,
    setIsForgotPasswordRequestSuccess,
  } = useForgotPasswordPageContext()

  const forgotPasswordSwr = useApiSwrForgotPasswordRequestMutation()

  const intl = useIntl()
  const formState = useMemo(
    () => makeForgotPasswordFormState(intl),
    [intl],
  ).use()
  const { model } = formState

  const [isFriendlyCaptchaDone, setIsFriendlyCaptchaDone] =
    useState<boolean>(false)

  const captchaDoneCallback = useCallback(
    (fcSolution: string): void => {
      formState.setValue(fcSolution, model, 'fcSolution')
      setIsFriendlyCaptchaDone(true)
    },
    [formState, model],
  )

  return (
    <form
      className="mx-auto mb-6 flex flex-col rounded-m bg-light-brand-medium p-4 desktop-lg:container tablet:w-3/5"
      data-qa-id="forgotPassword-form"
      noValidate
      onSubmit={(event): void => {
        event.preventDefault()
        void handleSubmit({
          forgotPasswordSwr,
          formState,
          setEmail,
          setIsForgotPasswordFormSubmitted,
          setIsForgotPasswordRequestSuccess,
        })
      }}
    >
      <p className="pb-6 text-s text-dark-primary-max">
        <FormattedMessage id="forgotPassword.information.message" />
      </p>
      <FormControl
        error={!model.validator.isPropertyValid('email')}
        {...formState.makeInputProps('email')}
        required
      >
        <FormLabel htmlFor="forgotPasswordEmail">
          <FormattedMessage id="general.label.email" />
        </FormLabel>
        <Input
          id="forgotPasswordEmail"
          slotProps={{
            input: { 'data-qa-id': 'forgotPassword-form-email' },
          }}
        />
        {model.validator.getFirstErrorFor('email') && (
          <FormHelperText data-qa-id="forgotPasswordEmail-error-msg">
            {model.validator.getFirstErrorFor('email')}
          </FormHelperText>
        )}
      </FormControl>
      <FriendlyCaptchaChallenge
        doneCallback={captchaDoneCallback}
        startMode="auto"
        whenThisPropChangesResetCaptcha={forgotPasswordSwr.error}
      />
      <LoadingButton
        className="w-full"
        data-qa-id="forgotpassword-form-button"
        disabled={!isFriendlyCaptchaDone || forgotPasswordSwr.isMutating}
        id="forgotpassword-form-button"
        loading={forgotPasswordSwr.isMutating}
        loadingPosition="end"
        type="submit"
      >
        <FormattedMessage id="forgotPassword.form.submit" />
      </LoadingButton>
    </form>
  )
}
